
import { Printer, Heart, Share2, Edit, Trash } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useState, useEffect } from "react";

interface RecipeHeaderActionsProps {
  shareUrl: string;
  isLiked: boolean;
  onLike: () => void;
  onPrint: () => void;
  recipeId: string;
  userId: string;
  disableLike?: boolean;
}

const RecipeHeaderActions = ({
  shareUrl,
  isLiked,
  onPrint,
  recipeId,
  userId,
  disableLike = false,
}: RecipeHeaderActionsProps) => {
  const navigate = useNavigate();
  const [isCreator, setIsCreator] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);

  useEffect(() => {
    checkIsCreator();
    checkIfFavorited();
  }, [userId]);

  const checkIsCreator = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setIsCreator(user?.id === userId);
  };

  const checkIfFavorited = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: favorite, error } = await supabase
        .from('favorites')
        .select('id')
        .eq('user_id', user.id)
        .eq('recipe_id', recipeId)
        .maybeSingle();

      if (error) {
        console.error('Error checking favorite status:', error);
        return;
      }

      setIsFavorited(!!favorite);
    } catch (error) {
      console.error('Error checking favorite status:', error);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  const handleFavorite = async () => {
    if (!recipeId) {
      toast.error("Demo recipes cannot be favorited");
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Please sign in to favorite recipes");
        return;
      }

      if (isFavorited) {
        // Remove from favorites
        const { error: favError } = await supabase
          .from('favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('recipe_id', recipeId);

        // Also remove the like
        const { error: likeError } = await supabase
          .from('likes')
          .delete()
          .eq('user_id', user.id)
          .eq('recipe_id', recipeId);

        if (favError || likeError) {
          console.error('Error removing favorite/like:', favError || likeError);
          toast.error("Failed to remove from favorites");
          return;
        }

        setIsFavorited(false);
        toast.success("Recipe removed from favorites");
      } else {
        // Add to favorites
        const { error: favError } = await supabase
          .from('favorites')
          .insert([{ 
            user_id: user.id, 
            recipe_id: recipeId 
          }]);

        // Also add a like
        const { error: likeError } = await supabase
          .from('likes')
          .insert([{
            user_id: user.id,
            recipe_id: recipeId
          }]);

        if (favError || likeError) {
          console.error('Error adding favorite/like:', favError || likeError);
          toast.error("Failed to add to favorites");
          return;
        }

        setIsFavorited(true);
        toast.success("Recipe added to favorites");
      }
    } catch (error) {
      console.error('Error updating favorite status:', error);
      toast.error("Failed to update favorite status");
    }
  };

  const handleEdit = () => {
    navigate(`/edit-recipe/${recipeId}`);
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('recipes')
        .delete()
        .eq('id', recipeId);

      if (error) {
        console.error('Error deleting recipe:', error);
        toast.error("Failed to delete recipe");
        return;
      }

      toast.success("Recipe deleted successfully");
      navigate('/dashboard');
    } catch (error) {
      console.error('Error in delete process:', error);
      toast.error("Failed to delete recipe");
    } finally {
      setShowDeleteDialog(false);
    }
  };

  return (
    <div className="flex items-center gap-2 sm:gap-4">
      {isCreator && (
        <>
          <Button variant="outline" size="icon" onClick={handleEdit}>
            <Edit className="w-4 h-4" />
          </Button>
          <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="text-red-500 hover:text-red-600"
              >
                <Trash className="w-4 h-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Recipe</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete this recipe? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleDelete}>
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
      {!disableLike && (
        <Button
          variant="outline"
          size="icon"
          onClick={handleFavorite}
          className={isFavorited ? "text-primary" : ""}
        >
          <Heart className="w-4 h-4" fill={isFavorited ? "currentColor" : "none"} />
        </Button>
      )}
      <Dialog>
        <DialogTrigger asChild>
          <Button variant="outline" size="icon">
            <Share2 className="w-4 h-4" />
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share Recipe</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="flex items-center space-x-2">
              <Input value={shareUrl} readOnly />
              <Button onClick={handleShare}>Copy</Button>
            </div>
            <div className="flex justify-center space-x-4">
              <Button
                variant="outline"
                onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(shareUrl)}`, '_blank')}
              >
                WhatsApp
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')}
              >
                Facebook
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`, '_blank')}
              >
                Twitter
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Button
        onClick={onPrint}
        variant="outline"
        className="hidden sm:flex items-center space-x-2"
      >
        <Printer className="w-4 h-4" />
        <span>Print Recipe</span>
      </Button>
      <Button
        onClick={onPrint}
        variant="outline"
        size="icon"
        className="sm:hidden"
      >
        <Printer className="w-4 h-4" />
      </Button>
    </div>
  );
};

export default RecipeHeaderActions;
